/**
 * client/src/lib/use-step-up-aal3-types.ts — wire shapes shared between
 * use-step-up-aal3.ts (the hook + single-assertion arm) and
 * use-step-up-aal3-dual.ts (the dual-passkey arm). 300-line modularity split.
 */

import type { PublicKeyCredentialRequestOptionsJSON } from "@simplewebauthn/browser";

export interface StepUpBeginResponse {
  /** Which basis this ceremony resolved to. Absent (or anything other than
   *  the literal "dual") is read as the single-assertion arm. */
  mode?: "hardware" | "dual" | "single" | "platform_tpm";
  options: PublicKeyCredentialRequestOptionsJSON;
}

export interface StepUpDualNextResponse {
  options: PublicKeyCredentialRequestOptionsJSON;
}

export interface StepUpCompleteResponse {
  success: boolean;
  newAal: "AAL2" | "AAL3";
}

/** Which leg of a (possibly dual) ceremony is currently in flight. "idle"
 *  covers both "not running" and the single-assertion arm's one prompt. */
export type StepUpPhase = "idle" | "first-key" | "second-key";

/**
 * The in-flight button label for a step-up phase.
 *
 * Lives next to `StepUpPhase` because these strings ARE that type's meaning —
 * "first-key"/"second-key" exist so a surface can tell the user a SECOND tap
 * is coming. Two components had independently copied the same three-branch
 * ternary, so adding a phase (or rewording a prompt) meant finding both with
 * no compile-time link between them.
 *
 * `phase` is optional: a hook mock predating the dual-passkey addition
 * supplies none, and falls through to the generic label rather than throwing.
 */
export function stepUpRunningLabel(phase?: StepUpPhase): string {
  if (phase === "first-key") return "Verifying your first passkey…";
  if (phase === "second-key") return "Now tap your second passkey…";
  return "Verifying…";
}
